import { useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { FloatingLeaves } from "./FloatingLeaves";
import { HERO_BG, HERO_MID } from "@/data/content";

gsap.registerPlugin(ScrollTrigger, useGSAP);

export const Hero = () => {
  const root = useRef(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
      tl.to(".hero-bg", { yPercent: 25, ease: "none" }, 0)
        .to(".hero-mid", { yPercent: 12, ease: "none" }, 0)
        .to(".hero-content", { yPercent: -18, opacity: 0.2, ease: "none" }, 0);
    },
    { scope: root }
  );

  return (
    <section
      id="hero"
      ref={root}
      data-testid="hero-section"
      className="relative h-screen min-h-[680px] w-full overflow-hidden bg-ink grain"
    >
      <div
        className="hero-bg absolute inset-0 -top-24 bg-cover bg-center scale-110"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div
        className="hero-mid absolute inset-0 -top-12 bg-cover bg-center opacity-40 mix-blend-luminosity"
        style={{ backgroundImage: `url(${HERO_MID})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/50 to-ink" />
      <div className="absolute inset-0 bg-forest/20" />

      <FloatingLeaves count={16} />

      <div className="hero-content relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-sm uppercase tracking-[0.35em] text-gold font-semibold mb-6"
        >
          Premium Landscaping · Brantford, Ontario
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-cream text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight max-w-4xl text-balance"
        >
          Where Every Yard <br className="hidden sm:block" />
          Becomes a <span className="text-gold italic">Sanctuary</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-8 max-w-xl text-cream/75 text-base lg:text-lg leading-relaxed"
        >
          Architect-grade landscape design, meticulous care and year-round service
          for discerning homeowners across Brantford and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#quote"
            data-testid="hero-quote-btn"
            className="bg-gold text-ink px-9 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors duration-300 text-center"
          >
            Get a Free Quote
          </a>
          <a
            href="#gallery"
            data-testid="hero-work-btn"
            className="border border-gold text-gold px-9 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors duration-300 text-center"
          >
            View Our Work
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#services"
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-gold"
      >
        <ChevronDown className="animate-bounce" size={30} />
      </motion.a>
    </section>
  );
};
